import { AxiosInstance } from 'axios';
import ChatRepositoryInterface, {
  ChatInterface,
  ChatScoreRequestInterface,
} from '@/services/QA/chat/interfaces';

export default class ChatRepository implements ChatRepositoryInterface {
  constructor(
    private axios: AxiosInstance,
  ) {
  }

  public async getLastUnreviewedChat(chatId?: string): Promise<ChatInterface> {
    const url = chatId ? `/qa-controller/chat/last?id=${chatId}` : '/qa-controller/chat/last';
    const res = await this.axios.get(url);

    return res.data;
  }

  public async getAllChats(): Promise<Array<Partial<ChatInterface>>> {
    const res = await this.axios.get('/qa-controller/chat');

    return res.data;
  }

  public async getOneById(chatId: string): Promise<ChatInterface> {
    const res = await this.axios.get(`/qa-controller/chat/${chatId}`);

    return res.data;
  }

  public async getAllReviewedChats(
    page: number,
    sortBy: string,
    sortDesc: boolean,
  ): Promise<[Array<Partial<ChatInterface>>, number]> {
    const sortDirection = sortDesc ? 'DESC' : 'ASC';
    const url = `/qa-controller/chat/reviewed?page=${page}&sortBy=${sortBy}&direction=${sortDirection}`;
    const res = await this.axios.get(url);

    return res.data;
  }

  public async getAllUnreviewedChats(
    page: number,
    sortBy: string,
    sortDesc: boolean,
  ): Promise<[Array<Partial<ChatInterface>>, number]> {
    const sortDirection = sortDesc ? 'DESC' : 'ASC';
    const url = `/qa-controller/chat/unreviewed?page=${page}&sortBy=${sortBy}&direction=${sortDirection}`;
    const res = await this.axios.get(url);

    return res.data;
  }

  public async getUnfinishedReviews(): Promise<Partial<ChatInterface>> {
    const res = await this.axios.get('/qa-controller/chat/user');

    return res.data;
  }

  public async reviewChat(
    chatId: string,
    agents: string[],
    scores: ChatScoreRequestInterface,
    tags: number[],
    sendStats: boolean = true,
    reviewLater: boolean = false,
  ): Promise<void> {
    await this.axios.patch(`/qa-controller/chat/${chatId}`, {
      agents,
      sendStats,
      reviewLater,
      data: { scores, tags },
    });
  }

  public async searchChats(
    currentPage: number,
    search: string,
    sortBy: string,
    sortDesc: boolean,
  ): Promise<void> {
    const sortDirection = sortDesc ? 'DESC' : 'ASC';
    const baseUrl = `/qa-controller/chat/search/${encodeURIComponent(search)}`;
    const urlParams = `?page=${currentPage}&sortBy=${sortBy}&direction=${sortDirection}`;
    const response = await this.axios.get(`${baseUrl}${urlParams}`);

    return response.data;
  }


  public async syncChats(): Promise<void> {
    await this.axios.post('/qa-controller/chat/sync');
  }

  public async clearChatQueue(timestamp: number, isDryRun: boolean): Promise<number> {
    const res = await this.axios.post('/qa-controller/chat/clear', { timestamp, isDryRun });

    return res.data;
  }

  public async getAgentChatIds(
    email: string,
    page: number,
    sortBy: string,
    sortDesc: boolean,
    reviewedIds: string[] = [],
  ): Promise<[string[], number]> {
    const sortDirection = sortDesc ? 'DESC' : 'ASC';
    const url = `/chat-controller/chats/agent?page=${page}&sortBy=${sortBy}&direction=${sortDirection}`;
    const response = await this.axios.post(url, { email, reviewedIds });

    return response.data;
  }

  public async getAgentUnreviewedChats(
    chatIds: string[],
    page: number,
  ): Promise<[Array<Partial<ChatInterface>>, number]> {
    const response = await this.axios.post(`/qa-controller/chat/unreviewed?page=${page}`, chatIds);

    return response.data;
  }
}
